import { jobStatusConfig } from '@/constants/job-constants';
import { KText } from '@/ui-library/components';
import { Box, Center, Flex } from '@chakra-ui/react';
import {
  Briefcase01,
  ClipboardCheck,
  LayersThree02,
  MarkerPin01,
} from '@kula-ai/icons-core';
import { IconText } from '../IconText';
import { type JobItem as JobItemType } from '../types';
import { SearchItemWrapper } from './SearchItemWrapper';
import { Link } from 'react-router-dom';
import { useSearchManager } from '../search-manager';
import { HighlightedTooltipText } from '../text';
import { DepartmentPath } from '@/components/shared/departments/DepartmentPath';
import { colors } from '@/theme/foundations/colors';

/**
 * The requisitions the search payload carries on a job. Declared here rather than beside the other
 * search-result types because it is read only by this component.
 */
interface JobRequisition {
  code?: string;
  requisition_value?: string;
  confidential?: boolean;
}

interface JobItemProps {
  item: JobItemType & { requisitions?: JobRequisition[] };
}

/**
 * A job can carry several requisitions (a headcount group is one row per hire), so show the one the
 * search term actually matched rather than whichever happens to come first.
 */
const matchedRequisition = (
  requisitions: JobRequisition[],
  searchTerm: string
): JobRequisition | undefined => {
  const term = searchTerm.trim().toLowerCase();
  const matched = term
    ? requisitions.find((requisition) =>
        requisition.requisition_value?.toLowerCase().includes(term)
      )
    : undefined;

  return matched ?? requisitions[0];
};

export const JobItem = ({ item }: JobItemProps) => {
  const {
    name,
    state,
    department = '',
    offices = [],
    requisitions = [],
    id,
  } = item;
  const { color = 'pastel-2-light', label = 'Open' } =
    jobStatusConfig?.[state] || {};
  const { onSearchClose, searchTerm } = useSearchManager();
  const requisition = matchedRequisition(requisitions, searchTerm);

  return (
    <Link
      to={`/jobs/${id}`}
      style={{ textDecoration: 'none' }}
      onClick={() => {
        onSearchClose();
      }}
    >
      <SearchItemWrapper>
        <Flex alignItems="center" gap="12px">
          <Center
            width="36px"
            height="36px"
            borderRadius="xs"
            bg="pastel-7-light"
          >
            <LayersThree02
              size="1rem"
              color="var(--kula-colors-pastel-7-dark)"
              className="kula-icon"
            />
          </Center>
          <Flex flexDirection="column" gap="3px" overflow="hidden">
            <Flex gap="6px" overflow="hidden">
              <HighlightedTooltipText
                variant="heading_3"
                color="primary-900"
                text={name}
                maxW="100%"
                highlight={searchTerm}
              />
              <KText
                variant="paragraph"
                color="secondary-700"
                p="1px 8px"
                borderRadius="xs"
                border="1px solid"
                borderColor="secondary-100"
                textTransform="capitalize"
                display="flex"
                alignItems="center"
                gap="4px"
                lineHeight="20px"
                whiteSpace="nowrap"
              >
                <Box bg={color} borderRadius="full" w="8px" h="8px" />
                {label}
              </KText>
            </Flex>
            <Flex gap="12px">
              {/* The department with its hierarchy (CORE-5596); search
                  results carry the name, which DepartmentPath resolves. */}
              {department && (
                <Flex alignItems="center" gap="6px">
                  <Briefcase01 size={16} color={colors['secondary-500']} />
                  <DepartmentPath department={{ name: department }} />
                </Flex>
              )}
              {offices.length > 0 && (
                <IconText
                  icon={MarkerPin01}
                  text={offices.join(', ')}
                  wrapperProps={{ gap: '4px' }}
                />
              )}
              {requisition?.requisition_value && (
                <IconText
                  icon={ClipboardCheck}
                  text={requisition.requisition_value}
                  highlight={searchTerm}
                  wrapperProps={{ gap: '4px' }}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      </SearchItemWrapper>
    </Link>
  );
};
